import React, { useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  IconButton,
  DialogContent,
  DialogActions,
  DialogTitle,
  TextField,
  Alert,
  ListItemText,
  styled,
  ListItem,
  Stack,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { Box, Button } from 'components/index';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { InputQCCReportDTO } from './quarterly-cc-report.type';
import { FilterConfig } from '../custom-filter-panel/DynamicFilters';
import { getProjectFiltersConfig } from '../project/project.type';
import useLookups from '../../hooks/useLookups';
import { LOOKUP_KEYS } from '../../common/constants/lookups';
import { City, County, CustomArea, State } from '../lookups/lookups.type';
import { mapCityLookup, mapCountyLookup, mapCustomAreaLookup, mapStateLookup } from '../lookups/lookups.util';
import CustomFilters, { CustomFiltersRef } from '../custom-filter-panel/CustomFilters';
import { deepCopy } from '../../common/utils/common.util';
import { fetchProjects } from '../project/project.api';
import { useQuery } from 'react-query';
import dataKeys from '../../common/react-query/data-keys';
import { toast } from 'react-toastify';
import { buildSearchRequestBody } from '../../common/utils/azure-search.util';

interface InputQCCReportDialogProps {
  open: boolean;
  onClose: () => void;
  initialValues?: InputQCCReportDTO;
  setValues: (values: InputQCCReportDTO) => void;
  loading?: boolean;
}

const InputQCCReportDialog: React.FC<InputQCCReportDialogProps> = ({ open, onClose, initialValues, setValues, loading }) => {
  const [filterString, setFilterString] = React.useState<any[]>([]);
  const [rawFilters, setRawFilters] = React.useState<any>(undefined);

  const defaultOnDisplayFilters = ['City', 'County', 'State', 'CustomArea', 'PostalCode', 'ProjectType', 'CommencementDate'];
  const stateOptions = useLookups(LOOKUP_KEYS.States) as State[];
  const cityOptions = useLookups(LOOKUP_KEYS.Cities) as City[];
  const countyOptions = useLookups(LOOKUP_KEYS.Counties) as County[];
  const customAreaOptions = useLookups(LOOKUP_KEYS.CustomAreas) as CustomArea[];
  const projectFiltersConfig = getProjectFiltersConfig({
    stateOptions: stateOptions.map(mapStateLookup),
    cityOptions: cityOptions.map(mapCityLookup),
    countyOptions: countyOptions.map(mapCountyLookup),
    customAreaOptions: customAreaOptions.map(mapCustomAreaLookup),
  });

  const customFiltersRef = useRef<CustomFiltersRef>(null);

  // Initialize temporary states when dialog opens
  useEffect(() => {
    if (open) {
      if (customFiltersRef.current) customFiltersRef.current.populateTempValues();
    }
  }, [open]);

  const getFiltersConfig = (): FilterConfig[] => {
    if (!defaultOnDisplayFilters) return projectFiltersConfig;
    const newConfig = deepCopy(projectFiltersConfig);
    newConfig.forEach((filter: any) => (filter.onDisplay = defaultOnDisplayFilters.includes(filter.propertyName)));
    return newConfig;
  };

  const handleFilterChange = (newFilterArray: any[] | '', rawFilters: any) => {
    if (newFilterArray) {
      const currentFilters = [...newFilterArray];
      setFilterString(currentFilters);
      setRawFilters(rawFilters);
    }
  };

  const fetchProjectsWrapper = useCallback(async (filterString: any) => {
    if (filterString == '') return [];
    try {
      const paginationModel = { page: 0, pageSize: 0 }; // set to anything - ignoring pagination before searching
      const sortingModel: any[] = []; // no sorting needed

      const searchRequestBody = buildSearchRequestBody('', filterString, paginationModel, sortingModel, true);
      const response = await fetchProjects(searchRequestBody);
      return response ?? []; // Return an empty array if response is undefined
    } catch (error) {
      // Handle or log the error here
      console.error(error);
      return []; // Return an empty array in case of an error
    }
  }, []);

  const { data, isLoading, isFetching, isError, error } = useQuery([dataKeys.allProjects, filterString], () =>
    fetchProjectsWrapper(filterString),
  );

  // Handle projects and total values
  let projects: any[] = [];
  let projectsCount = 0;
  if (data && data.error) {
    toast.error(data?.error?.message);
    projects = [];
  } else {
    // Use the existing logic for setting projects
    projects = isError ? [] : data && data.value && Array.isArray(data.value) ? data.value : [];
    projectsCount = isError ? 0 : data && data['@odata.count'] ? data['@odata.count'] : 0;
  }

  const handleSetValues = (values: Partial<InputQCCReportDTO>) => {
    const newValues: InputQCCReportDTO = {
      name: values.name ?? '',
      compIds: (projects ?? []).map(project => project.Id),
      filtersString: JSON.stringify(rawFilters),
    };
    setValues(newValues);
  };

  const initialOrDefaultValues: Partial<InputQCCReportDTO> = {
    name: initialValues?.name ?? '',
  };

  const formik = useFormik({
    initialValues: initialOrDefaultValues,
    enableReinitialize: true,
    validationSchema: yup.object({
      name: yup.string().matches(/\S/, 'Name cannot be empty or just spaces').required('Required'),
    }),
    onSubmit: values => {
      handleSetValues(values);
    },
  });

  useEffect(() => {
    if (open) {
      formik.resetForm();
    }
  }, [open]);

  const canCreateReport =
    formik.dirty && formik.isValid && !isLoading && !isFetching && projectsCount != 0 && requiredFiltersExist(filterString);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>Enter Report Data</DialogTitle>
      <IconButton onClick={onClose} style={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ paddingTop: 0 }}>
        <form onSubmit={formik.handleSubmit}>
          <StyledListItem>
            <Box flex={2} pr={10}>
              <ListItemText primary={'Report Name'} primaryTypographyProps={{ style: { fontWeight: 'bold', fontSize: 16 } }} />
            </Box>
            <Box flex={6}>
              <Stack key={'name'} flexDirection={'row'} spacing={1} alignItems={'top'}>
                <Box flex={5}>
                  <TextField
                    fullWidth
                    margin='normal'
                    label='Report Name'
                    name='name'
                    required
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={e => {
                      formik.setFieldValue('name', e.target.value.trim());
                      formik.handleBlur(e);
                    }}
                    error={formik.touched.name && Boolean(formik.errors.name)}
                    helperText={formik.touched.name && formik.errors.name}
                  />
                </Box>
                <Box flex={0.5} pl={1} pt={2}>
                  <Tooltip
                    title='Clear'
                    arrow={false}
                    onClick={() => {
                      formik.setFieldValue('name', '');
                    }}
                  >
                    <IconButton aria-label='close' size='small'>
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
            </Box>
          </StyledListItem>
          <CustomFilters
            ref={customFiltersRef}
            filtersConfig={getFiltersConfig()}
            onFilterChange={handleFilterChange}
            prevAppliedFilters={undefined}
            autoApplyOnChange={true}
          />
        </form>
        {renderProjectsCount(projectsCount, isLoading || isFetching, filterString)}
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' color='primary' onClick={formik.handleSubmit} disabled={!canCreateReport} loading={loading}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const renderProjectsCount = (count: number, loading: boolean, filtersArray: string[]) => {
  if (filtersArray.length == 0 || !requiredFiltersExist(filtersArray)) {
    return (
      <Alert severity={'warning'} sx={{ marginTop: 2 }}>
        <b>Please select one or more Project Types and Locations</b>
      </Alert>
    );
  }

  if (loading) {
    return <LinearProgress sx={{ marginTop: 2 }} />;
  }

  let message = count === 0 ? 'No projects' : count == 1 ? `One project` : `${count} projects`;
  message += ' found matching this criteria';

  const alertType = count === 0 ? 'error' : 'info';

  return (
    <Alert severity={alertType} sx={{ marginTop: 2 }}>
      <b>{message}</b>
    </Alert>
  );
};

const requiredFiltersExist = (filters: string[]) => {
  return (
    filters.some(filterString => filterString.includes('ProjectType')) &&
    filters.some(
      filterString =>
        filterString.includes('City') ||
        filterString.includes('County') ||
        filterString.includes('State') ||
        filterString.includes('CustomArea') ||
        filterString.includes('PostalCode'),
    )
  );
};

const StyledListItem = styled(ListItem)({
  width: '100%',
  cursor: 'pointer',
  paddingInline: '30px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '& .MuiListItemText-primary': {
    color: '#383838',
  },
});

export default InputQCCReportDialog;
